import type { QuestionConfig } from './QuestionsConfig'

export type MattressQuestions =
  | 'budget'
  | 'type'
  | 'share-bed'
  | 'sleep-position'
  | 'partner-sleep-position'
  | 'partner-movement'
  | 'weight-difference'
  | 'firmness'
  | 'aches-pains'
  | 'temperature-preference'
  | 'removable-cover'
  | 'green-choice'

const partnerQuestions: MattressQuestions[] = [
  'partner-sleep-position',
  'partner-movement',
  'weight-difference',
]

export const mattressBudgetQuestion: QuestionConfig<MattressQuestions> = {
  name: 'budget',
  title: 'Do you have a price in mind?',
  question: 'Select one option to indicate the maximum amount you want to spend',
  shortQuestion: 'budget',
  options: [
    {
      label: '£200',
      filter: {
        'price.amount': {
          lt: 200,
        },
      },
    },
    {
      label: '£400',
      filter: {
        'price.amount': {
          lt: 400,
        },
      },
    },
    {
      label: '£600',
      filter: {
        'price.amount': {
          lt: 600,
        },
      },
    },
    {
      label: '£800',
      filter: {
        'price.amount': {
          lt: 800,
        },
      },
    },
    {
      label: '£1,000',
      filter: {
        'price.amount': {
          lt: 1000,
        },
      },
    },
    {
      label: '£1,200',
      filter: {
        'price.amount': {
          lt: 1200,
        },
      },
    },
    {
      label: 'Unlimited',
      // No filter needed.
    },
  ],
}

export const mattressTypeQuestion: QuestionConfig<MattressQuestions> = {
  name: 'type',
  title: 'Which type of mattress',
  question: 'Let us know if you have a preference for the type of mattress you’ll buy.',
  shortQuestion: 'mattress type',
  options: [
    {
      label: 'Hybrid',
      filter: {
        'searchable_facets.tech_specs.type': 'Hybrid',
      },
      tooltip:
        'Pressure relieving foam and supportive springs for the best of both worlds (if the Which? test score is high). A bit pricier.',
    },
    {
      label: 'Pocket sprung',
      filter: {
        or: [
          {
            'searchable_facets.tech_specs.type': 'Pocket sprung',
          },
          {
            'searchable_facets.tech_specs.type': 'Pocket Sprung',
          },
        ],
      },
      tooltip:
        'Hundreds of individual pocket springs which react to your body weight independently. Breathable and cooler, but pricier.',
    },
    {
      label: 'Memory foam',
      filter: {
        'searchable_facets.tech_specs.type': 'Memory foam',
      },
      tooltip:
        'Moulds to your body shape for pressure relief. Feels warmer as you sink in, but can be more expensive.',
    },
    {
      label: 'Coiled spring',
      filter: {
        or: [
          {
            'searchable_facets.tech_specs.type': 'Open coil spring',
          },
          {
            'searchable_facets.tech_specs.type': 'Open coil',
          },
        ],
      },
      tooltip:
        'Made from a single, looped wire. Cheaper than other types and cool to lie on, but if you share a bed, the springs move as one unit.',
    },
  ],
}

export const mattressShareBedQuestion: QuestionConfig<MattressQuestions> = {
  name: 'share-bed',
  title: 'Do you share a bed?',
  question: 'This helps us recommend a mattress that’s the best fit for whoever’s sleeping in it.',
  shortQuestion: 'share bed',
  // No filtering is required here. Only for navigation purposes in the journey.
  options: [
    {
      label: 'I sleep alone',
      skipSteps: partnerQuestions,
    },
    {
      label: 'I share my bed',
    },
  ],
}

export const mattressSleepPositionQuestion: QuestionConfig<MattressQuestions> = {
  name: 'sleep-position',
  title: 'What’s your sleeping position?',
  question: 'The support your mattress gives you depends on how you’ll sleep on it.',
  shortQuestion: 'sleep position',
  options: [
    {
      label: 'On my back',
      filter: {
        'searchable_facets.test_results.body_support_lying_on_back': {
          gte: 4,
        },
      },
    },
    {
      label: 'On my side',
      filter: {
        'searchable_facets.test_results.body_support_lying_on_side': {
          gte: 4,
        },
      },
    },
    {
      label: 'On my front',
      filter: {
        'searchable_facets.test_results.body_support_lying_on_front': {
          gte: 4,
        },
      },
    },
    {
      label: 'A combination of the above',
      filter: {
        'searchable_facets.test_results.body_support_overall': {
          gte: 4,
        },
      },
    },
  ],
}

export const mattressPartnerSleepPositionQuestion: QuestionConfig<MattressQuestions> = {
  name: 'partner-sleep-position',
  title: 'And how about your partner?',
  question:
    'We want to ensure everyone who uses the mattress is comfortable, so it helps to know how your partner sleeps, too. Different positions need different levels of support to make sure you both wake up feeling refreshed!',
  shortQuestion: 'partner sleep position',
  options: [
    {
      label: 'On their back',
      filter: {
        'searchable_facets.test_results.body_support_lying_on_back': {
          gte: 4,
        },
      },
    },
    {
      label: 'On their side',
      filter: {
        'searchable_facets.test_results.body_support_lying_on_side': {
          gte: 4,
        },
      },
    },
    {
      label: 'On their front',
      filter: {
        'searchable_facets.test_results.body_support_lying_on_front': {
          gte: 4,
        },
      },
    },
    {
      label: 'A combination of the above',
      filter: {
        'searchable_facets.test_results.body_support_overall': {
          gte: 4,
        },
      },
    },
  ],
}

export const mattressPartnerMovementQuestion: QuestionConfig<MattressQuestions> = {
  name: 'partner-movement',
  title: 'Does partner movement wake you?',
  question:
    'Even if you’re both in the right position, a restless partner can still disturb your rest. We’ll use this to find a mattress that stays nice and still on your side, so you can both sleep soundly.',
  shortQuestion: 'partner movement',
  options: [
    {
      label: 'Yes',
      filter: {
        'searchable_facets.test_results.stability': {
          gte: 4,
        },
      },
    },
    {
      label: 'No',
      // No filter needed.
    },
  ],
}

export const mattressWeightDifferenceQuestion: QuestionConfig<MattressQuestions> = {
  name: 'weight-difference',
  title: 'Big difference in weight?',
  question:
    'When there is a difference of 20kg or more between partners, we look for mattresses that offer independent support. This ensures the surface stays level and comfy for both of you.',
  shortQuestion: 'weight difference',
  options: [
    {
      label: 'Yes',
      filter: {
        'searchable_facets.test_results.stability': {
          gte: 4,
        },
      },
    },
    {
      label: 'No',
      // No filter needed.
    },
  ],
}

export const mattressFirmnessQuestion: QuestionConfig<MattressQuestions> = {
  name: 'firmness',
  title: 'Mattress firmness',
  question: 'If you know how firm you like your mattress to be, let us know.',
  shortQuestion: 'mattress firmness',
  options: [
    {
      label: 'Soft',
      filter: {
        'searchable_facets.tech_specs.softness': {
          gte: 7,
          lte: 10,
        },
      },
    },
    {
      label: 'Medium',
      filter: {
        'searchable_facets.tech_specs.softness': {
          gte: 5,
          lte: 7,
        },
      },
    },
    {
      label: 'Firm',
      filter: {
        'searchable_facets.tech_specs.softness': {
          gte: 1,
          lte: 5,
        },
      },
    },
  ],
}

export const mattressAchesPainsQuestion: QuestionConfig<MattressQuestions> = {
  name: 'aches-pains',
  title: 'Do you get any aches or pains?',
  question:
    'We want to make sure you feel secure and supported while you sleep. Telling us about any niggles helps us narrow down the specific cushioning you need to wake up feeling refreshed.',
  shortQuestion: 'aches or pains',
  options: [
    {
      label: 'Yes',
      filter: {
        'searchable_facets.test_results.pressure_distribution': {
          gte: 4,
        },
      },
    },
    {
      label: 'No',
      // No filter is needed.
    },
  ],
}

export const mattressTemperaturePreferenceQuestion: QuestionConfig<MattressQuestions> = {
  name: 'temperature-preference',
  title: 'Temperature preference',
  question:
    'Some people love a mattress that feels snug and warm, while others prefer the cool feeling of a fresh bed. What is your preference?',
  shortQuestion: 'temperature preference',
  options: [
    {
      label: 'I prefer to stay cool',
      filter: {
        or: [
          {
            'searchable_facets.tech_specs.warmth': 'Cold',
          },
          {
            'searchable_facets.tech_specs.warmth': 'Very cold',
          },
        ],
      },
    },
    {
      label: 'I like a nice average temperature',
      filter: {
        'searchable_facets.tech_specs.warmth': 'Average warmth',
      },
    },
    {
      label: 'I like to feel warm and cosy',
      filter: {
        or: [
          {
            'searchable_facets.tech_specs.warmth': 'Warm',
          },
          {
            'searchable_facets.tech_specs.warmth': 'Very warm',
          },
        ],
      },
    },
  ],
}

export const mattressRemovableCoverQuestion: QuestionConfig<MattressQuestions> = {
  name: 'removable-cover',
  title: 'Removable mattress cover',
  question: 'Do you want a removable mattress cover, and do you want it washable?',
  shortQuestion: 'removable cover',
  options: [
    {
      label: 'Yes, removable',
      filter: {
        'searchable_facets.tech_specs.removable_mattress_cover': 'true',
      },
    },
    {
      label: 'Yes, removable and washable',
      filter: {
        and: [
          {
            'searchable_facets.tech_specs.removable_mattress_cover': 'true',
          },
          {
            'searchable_facets.test_results.washability': {
              gte: 3,
            },
          },
        ],
      },
    },
    {
      label: 'No, not important to me',
      // No filter needed.
    },
  ],
}

export const mattressGreenChoiceQuestion: QuestionConfig = {
  name: 'green-choice',
  title: 'Is a green choice important',
  question:
    'We also track the environmental impact of every mattress. Would you like us to prioritise models that are ethically made or easier to recycle?',
  shortQuestion: 'green choice',
  options: [
    {
      label: 'Yes, show me the eco-friendly options',
      filter: {
        'searchable_facets.test_results.sustainability_overall': {
          gte: 4,
        },
      },
    },
    {
      label: 'No, not important',
      // No filter needed.
    },
  ],
}
